import { AsyncPipe, NgStyle } from '@angular/common';
import { Component, computed, DestroyRef, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule, NgForm } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { ChildOnboardingSituationDocument } from '@zero/onboarding/domain';
import { ButtonComponent, CheckboxComponent, Item, SelectComponent } from '@zero/shared/ui-web-design-system';
import { FileToUpload } from '@zero/shared/util-models';
import { TicketDialogService } from '@zero/shared/util-web-app-services';
import { MAX_UPLOAD_FILE_SIZE } from 'constants/shared-constants';
import { combineLatest, filter, firstValueFrom, map } from 'rxjs';
import { BaseComponent } from '../../components/base.component';
import { OnboardingContainerComponent } from '../../components/onboarding-container/onboarding-container.component';

type TypedFileUpload = Record<string, { file: FileToUpload; confirmed: boolean }>;
@Component({
  imports: [
    FormsModule,
    OnboardingContainerComponent,
    NgStyle,
    TranslocoPipe,
    AsyncPipe,
    SelectComponent,
    ButtonComponent,
    CheckboxComponent,
  ],
  templateUrl: './document-upload.component.html',
  styleUrls: ['./document-upload.component.scss'],
})
export class DocumentUploadComponent extends BaseComponent {
  readonly #destroyRef = inject(DestroyRef);
  readonly #ticketDialogService = inject(TicketDialogService);

  public readonly hasFilesAlreadyUploaded = signal<boolean>(true);
  public readonly filesToUpload = signal<TypedFileUpload>({});
  public readonly filesToUploadKeys = computed(() => Object.keys(this.filesToUpload()));

  public documentTypeNameMap: Record<string, string>;
  public errors: string[] = [];
  public readonly situationItems = computed(() => {
    const situation = this.store.situations();
    const selectedSituation = this.store.selectedSituation();
    if (!situation?.length) {
      return [];
    }

    const selectedSituationType = selectedSituation?.type;
    return situation.map((situation) => ({
      label: situation.name,
      value: situation.type,
      selected: selectedSituationType === situation.type,
    }));
  });

  constructor() {
    super();

    effect(() => {
      const files = this.filesToUpload();
      if (!files) {
        return;
      }

      const values = Object.values(files);
      for (const value of values) {
        const file = value?.file;
        if (!file) {
          return;
        }

        if (file?.fileData && !file?.fileRefForTicket && !file?.uploadInProgress && file?.uploadProgress !== 100 && !file?.uploadError) {
          this.#uploadFile(file);
        }
      }
    });
  }

  #uploadFile(file: FileToUpload): void {
    this.#ticketDialogService.upload(file).subscribe(({ success, message }) => {
      !success && this.errors.push(message ?? `Datei ${file.fileName} konnte nicht hochgeladen werden.`);
    });
  }

  public selectSituation = (item: Item): void => {
    const selectedSituation = this.store.situations().find((s) => s.type === item.value) ?? null;
    this.store.setSelectedSituation(selectedSituation?.type ?? null);
    this.#setFilesToUploadBySituation(selectedSituation?.documents ?? []);
  };

  #setFilesToUploadBySituation(documents: ChildOnboardingSituationDocument[]): void {
    this.documentTypeNameMap = documents?.reduce(
      (acc, doc) => {
        acc[doc.type] = doc.name;
        return acc;
      },
      {} as Record<string, string>,
    );

    const situationDocumentTypes = documents?.map((d) => d.type) || [];
    this.filesToUpload.update((current) => {
      const preservedUploaded = situationDocumentTypes.reduce((acc, key) => {
        acc[key] = current[key] ?? this.filesToUpload[key];
        return acc;
      }, {} as TypedFileUpload);

      return {
        ...preservedUploaded,
      };
    });
  }

  public hasAllFilesUploadedAndConfirmed$ = toObservable(
    computed(() =>
      Object.values(this.filesToUpload() ?? {}).every(
        (value) =>
          value?.confirmed &&
          value?.file &&
          !!value?.file?.fileRefForTicket &&
          !value?.file?.uploadInProgress &&
          !value?.file?.uploadError &&
          value?.file?.uploadProgress === 100,
      ),
    ),
  );

  public readonly isFormValid$ = combineLatest([
    toObservable(this.hasFilesAlreadyUploaded),
    this.hasAllFilesUploadedAndConfirmed$,
    this.store.selectedSituation$,
    this.store.sendingIdentificationInformation$,
  ]).pipe(
    map(
      ([hasFilesAlreadyUploaded, hasAllFilesUploadedAndConfirmed, situation, sending]) =>
        hasFilesAlreadyUploaded || (hasAllFilesUploadedAndConfirmed && situation?.isValid && !sending),
    ),
  );

  protected override populateFromData(): void {
    combineLatest([this.store.selectedChildOnboarding$, this.store.situations$])
      .pipe(
        filter(([, situations]) => !!situations?.length),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(([{ childOnboardingSituation, uploadFileReferences }, situations]) => {
        this.hasFilesAlreadyUploaded.set(!!uploadFileReferences?.length);
        if (situations && childOnboardingSituation) {
          const selectedSituation = situations.find((s) => s.type === childOnboardingSituation) ?? null;
          this.store.setSelectedSituation(selectedSituation?.type ?? null);
          this.#setFilesToUploadBySituation(selectedSituation?.documents ?? []);
        }
      });
  }

  protected override listenToSuccessfulSubmit(): void {
    this.store.sentIdentificationInformationSuccessfully$
      .pipe(
        filter((sent) => sent),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => this.service.goToNextStep());
  }

  protected override async submit(): Promise<void> {
    const valid = await firstValueFrom(this.isFormValid$);
    if (!valid) {
      return;
    }

    const filesToUpload = this.filesToUpload();
    const keys = this.filesToUploadKeys();
    if (!filesToUpload || !keys) {
      return;
    }
    const files = keys?.length
      ? keys.map((key) => ({
          documentType: key,
          fileReference: filesToUpload[key]?.file?.fileRefForTicket,
        }))
      : [];

    this.store.sendIdentificationInformation(files, this.hasFilesAlreadyUploaded());
  }

  public submitFileUpload(key: string, fileUpload: NgForm, fileUploadElem: HTMLInputElement): void {
    this.errors = [];

    if (fileUploadElem.files.length > 1) {
      this.errors.push('child-onboarding.identification.file-upload.error.max-files');
      fileUpload.reset('tempFiles');
      return;
    }

    const fileData = fileUploadElem.files[0];
    if (fileData) {
      const fileTooLarge = fileData.size > MAX_UPLOAD_FILE_SIZE;
      const file = {
        fileData,
        uploadInProgress: false,
        uploadProgress: 0,
        fileName: fileData.name,
        fileRefForTicket: undefined,
        uploadError: fileTooLarge ? 'Maximale Dateigröße von 9MB überschritten.' : undefined,
      };
      this.filesToUpload.update((current) => {
        const newFiles = { ...current };
        newFiles[key] = { file, confirmed: false };
        return newFiles;
      });
    }
  }

  public deleteUploadedFile(fileToDelete: FileToUpload): void {
    this.#ticketDialogService.delete(fileToDelete).subscribe(() => {
      this.errors = [];
    });

    this.filesToUpload.update((current) => {
      const newFiles = { ...current };
      const fileKey = Object.keys(current).find((key) => current[key]?.file === fileToDelete);
      if (fileKey) {
        newFiles[fileKey] = null;
      }
      return newFiles;
    });
  }

  public toggleConfirmed(key: string, value: boolean): void {
    this.filesToUpload.update((current) => {
      const newFiles = { ...current };
      if (newFiles[key]) {
        newFiles[key].confirmed = value;
      }
      return newFiles;
    });
  }

  public restartUpload(): void {
    this.hasFilesAlreadyUploaded.set(false);
    this.filesToUpload.update((current) => {
      const newFiles = { ...current };
      Object.keys(current).forEach((key) => {
        newFiles[key] = null;
      });
      return newFiles;
    });
  }
}
